import { ChatGroup, GroupMember, ScopePermissionDefinition } from 'services/groups/types'
import { ChatUser } from 'services/users/types'
import actions from './actions'

/** Loose pagination shape — mirrors the API's `meta.pagination`, but several
 * sagas reset it to `[]` or `{}` on empty/error, so callers must not assume
 * every field is present. */
export interface LoosePagination {
  current_page?: number
  total?: number
  per_page?: number
  count?: number
  total_pages?: number
}

export interface GroupsState {
  groupsList: ChatGroup[]
  groupsActive: ChatGroup | unknown[]
  groupSearchText: string
  groupMemberSearchText: string
  groupBannedMemberSearchText: string
  groupMembersList: GroupMember[]
  groupBannedMembersList: GroupMember[]
  groupsListTotal: number
  groupMembersListTotal: number
  groupBannedMembersListTotal: number
  groupsFilters: Record<string, unknown>
  // App the saved filters belong to, so they never leak into another app.
  groupsFiltersAppId?: string | number
  groupsListPagination: LoosePagination
  groupMembersListPagination: LoosePagination | unknown[]
  groupBannedMembersListPagination: LoosePagination | unknown[]
  areGroupsLoading: boolean

  // Add-members modal source list — backed by services/users (see sagas.ts
  // NON_PARTICIPANTS_LIST/SEARCH_NON_PARTICIPANT), not a group-scoped endpoint.
  nonParticipantSearchText: string
  nonParticipantsList: ChatUser[]
  nonParticipantsListTotal: number
  nonParticipantsListPagination: LoosePagination

  // Scope/role permissions editor
  groupScopePermissionsList: Record<string, ScopePermissionDefinition> | unknown[]
  groupScopePermissionsListTotal: number
  groupScopePermissionsValues: Record<string, string | string[] | null>
}

// Trimmed per D3 (see actions.ts) — dropped keys the 6 out-of-scope handlers
// wrote to (groupsModerationList/Total/Active, groupModerationSearchText,
// groupUserSearchText, userNonGroupsList), plus groupMessageLogs/
// groupMessageLogsLoading/groupFriendSearchText, which were dead defaults
// unused by any of the 25 in-scope handlers either (confirmed via a full
// read of redux/groups/sagas.js).
const initialState: GroupsState = {
  groupsList: [],
  groupsActive: [],
  groupSearchText: '',
  groupMemberSearchText: '',
  groupBannedMemberSearchText: '',
  groupMembersList: [],
  groupBannedMembersList: [],
  groupsListTotal: 0,
  groupMembersListTotal: 0,
  groupBannedMembersListTotal: 0,
  groupsFilters: {},
  groupsListPagination: {},
  groupMembersListPagination: [],
  groupBannedMembersListPagination: [],
  areGroupsLoading: false,
  nonParticipantSearchText: '',
  nonParticipantsList: [],
  nonParticipantsListTotal: 0,
  nonParticipantsListPagination: {},
  groupScopePermissionsList: [],
  groupScopePermissionsListTotal: 0,
  groupScopePermissionsValues: {},
}

export default function groupsReducer(
  state: GroupsState = initialState,
  action: { type: string; payload?: Partial<GroupsState> },
): GroupsState {
  switch (action.type) {
    case actions.SET_STATE:
      return { ...state, ...action.payload }
    default:
      return state
  }
}
